/**
 * Centralized query key factory for TanStack Query.
 * Ensures consistent cache key structure across all features.
 *
 * Each feature namespace provides:
 *   - `all` — root key for bulk invalidation (e.g. `queryKeys.history.all`)
 *   - specific keys — for individual queries
 */
export const queryKeys = {
  generation: {
    all: ['generation'] as const,
    detail: (id: string) => ['generation', id] as const,
    list: ['generation', 'list'] as const,
    models: (type: string) => ['generation', 'models', type] as const,
    systemStats: ['generation', 'systemStats'] as const,
  },
  history: {
    all: ['history'] as const,
    list: (limit: number, offset: number, status?: string) =>
      ['history', 'list', { limit, offset, status }] as const,
    stats: ['history', 'stats'] as const,
  },
  resources: {
    all: ['resources'] as const,
    list: (type?: string) => ['resources', 'list', type] as const,
    search: (query: string, type?: string) =>
      ['resources', 'search', query, type] as const,
  },
  search: {
    all: ['search'] as const,
    models: (
      query: string,
      provider?: string,
      resourceType?: string,
      page?: number,
      pageSize?: number,
    ) => ['search', 'models', { query, provider, resourceType, page, pageSize }] as const,
    modelDetail: (provider: string, externalId: string) =>
      ['search', 'model-detail', provider, externalId] as const,
    providers: ['search', 'providers'] as const,
  },
  tags: {
    all: ['tags'] as const,
    search: (query: string, limit?: number, category?: number) =>
      ['tags', 'search', { query, limit, category }] as const,
    popular: (limit?: number, category?: number) =>
      ['tags', 'popular', { limit, category }] as const,
    categories: ['tags', 'categories'] as const,
    stats: ['tags', 'stats'] as const,
  },
  settings: {
    all: ['settings'] as const,
  },
} as const
